/* eslint-disable react-hooks/exhaustive-deps */
import { faArrowRight, faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";

import PurchasePlanApi from "../../api/PurchasePlanApi";
import FASvgIcon from "../../components/FASvgIcon";
import PriceDisplay from "../../components/PriceDisplay";
import RazorPayPayment from "../../components/RazorpayPayment";
import SvgImage from "../../components/SvgImage";
import { CardLoader } from "../../components/content-loader";
import { ButtonSpinner } from "../../components/placeholders";
import { appConstants } from "../../config/app-constants";
import {
  EToastvariant,
  ICreateEFPlanTxn,
  IPlanCheckoutForm,
  IVerifyCodeResp
} from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import { ITRFilingSelector, unsetCheckOutPlan } from "../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

const initialValues: IPlanCheckoutForm = {
  coupon: ""
};

const validationSchema = Yup.object({
  coupon: Yup.string().trim().notRequired()
});

type Props = {};

const PlanCheckout: React.FC<Props> = (props: Props) => {
  const dispatch = useAppDispatch();
  const { checkOutPlan } = useAppSelector(ITRFilingSelector);

  const navigate = useNavigate();
  const { filingUUID } = useParams();

  const initialVerifyCodeResp: IVerifyCodeResp = {
    subtotal: 0,
    total: 0,
    discount: 0
  };

  const [applyCoupon, setApplyCoupon] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [selectedPlanData, setSelectedPlanData] = useState<ICreateEFPlanTxn | null>(null);
  const [validCouponCode, setValidCouponCode] = useState<string | null>(null);
  const [verifyCodeResp, setVerifyCodeResp] = useState<IVerifyCodeResp>(initialVerifyCodeResp);
  const [initCouponValues, setInitCouponValues] = useState<IVerifyCodeResp>(initialVerifyCodeResp);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (!checkOutPlan) {
      navigate("/home", { replace: true });
      return;
    }
    getEFilingPlanPayableAmount();
  }, []);

  const getEFilingPlanPayableAmount = async () => {
    try {
      if (!filingUUID || !checkOutPlan?.uuid) {
        return;
      }
      setIsLoading(() => true);
      const plansApi = PurchasePlanApi.getInstance();
      const { subtotal } = await plansApi.getEFilingPlanPayableAmount({
        efilingUuid: filingUUID,
        planUuid: checkOutPlan?.uuid
      });
      setInitCouponValues(() => ({
        subtotal: subtotal,
        total: subtotal,
        discount: 0
      }));
      setVerifyCodeResp(() => ({
        subtotal: subtotal,
        total: subtotal,
        discount: 0
      }));
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsLoading(() => false);
    }
  };

  const handlePlanPurchase = async () => {
    try {
      if (!filingUUID || !checkOutPlan?.uuid) {
        return;
      }
      setIsProcessing(() => true);
      const plansApi = PurchasePlanApi.getInstance();
      const { subtotal } = await plansApi.createEfilingPlan({
        efilingUuid: filingUUID,
        planUuid: checkOutPlan?.uuid
      });

      const planData: ICreateEFPlanTxn = {
        efilingUuid: filingUUID,
        planUuid: checkOutPlan?.uuid,
        couponCode: validCouponCode
      };

      if (subtotal === 0) {
        await plansApi.createEfilingPlanTransaction(planData);
        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Success",
            message: "You have used first free filing",
            variant: EToastvariant.Success
          })
        );
        navigate(-1);
      } else {
        setSelectedPlanData(() => planData);
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsProcessing(() => false);
    }
  };

  const handleVerifyCouponCode = async (
    values: IPlanCheckoutForm,
    { setSubmitting }: FormikHelpers<IPlanCheckoutForm>
  ) => {
    try {
      if (!filingUUID || !checkOutPlan?.uuid) {
        return;
      }
      setSubmitting(true);
      await new Promise((r) => setTimeout(r, 1000));
      const plansApi = PurchasePlanApi.getInstance();
      const { subtotal, discount } = await plansApi.verifyCouponCode({
        efilingUuid: filingUUID,
        planUuid: checkOutPlan?.uuid,
        couponCode: values.coupon
      });
      setValidCouponCode(() => values.coupon);
      setVerifyCodeResp(() => ({
        subtotal: subtotal + discount,
        total: subtotal,
        discount: discount
      }));
      setIsDisabled(true);
      setApplyCoupon(true);
    } catch (error: any) {
      setValidCouponCode(() => null);
      if (error?.response?.status !== 500) {
        setApplyCoupon(false);
        setIsDisabled(true);
      } else {
        dispatch(
          showToastMsg({
            isVisible: true,
            title: "Error",
            message: UtilService.getErrorMessage(error),
            variant: EToastvariant.Danger
          })
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="checkout-page-wrap">
      <Container>
        <Row>
          <Col md="auto" className="col-left-wrap">
            <div className="content-wrap">
              <div className="img-wrap mb-5">
                <img
                  className="img-fluid"
                  src={`${appConstants.basePath}/assets/images/start-filing.svg`}
                  alt="start filling"
                />
              </div>
              <div className="feature-wrap">
                <h2 className="fw-bold mb-5 text-primary">
                  Choose <span className="text-secondary">your Plan</span>
                </h2>
                <div className="feature-item text-secondary">
                  <FASvgIcon icon={faArrowRight} />
                  <div className="feature-content">
                    <h4 className="text-secondary">Secure Payments</h4>
                    <p className="leading-para text-greyDark mb-0">
                      Our checkout process is equipped with the highest level of encryption and
                      security measures.
                    </p>
                  </div>
                </div>
                <div className="feature-item text-secondary">
                  <FASvgIcon icon={faArrowRight} />
                  <div className="feature-content">
                    <h4 className="text-secondary">Trusted Partners</h4>
                    <p className="leading-para text-greyDark mb-0">
                      Be assured that your payment is handled by industry-leading professionals.
                    </p>
                  </div>
                </div>
                <div className="feature-item text-secondary">
                  <FASvgIcon icon={faArrowRight} />
                  <div className="feature-content">
                    <h4 className="text-secondary">Responsive Customer Support</h4>
                    <p className="leading-para text-greyDark mb-0">
                      Any questions or concerns during the checkout process, our team is here to
                      assist you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Col>
          <Col className="col-right-wrap">
            <div className="form-wrap">
              <div className="payment-info">
                <SvgImage imgId="shield-icon" />
                <h4 className="fw-medium mb-0">100% Secure Payments</h4>
              </div>
              {isLoading ? (
                <CardLoader cardType="checkout" />
              ) : (
                <Card className="checkout-card">
                  <Card.Body>
                    <Card.Title className="text-primary">Checkout</Card.Title>
                    <div className="checkout-details">
                      <div className="plan-content">
                        <h4 className="fw-bold">{checkOutPlan?.title}</h4>
                        {checkOutPlan?.subTitle && (
                          <h5 className="fw-bold text-greyDark">{checkOutPlan?.subTitle}</h5>
                        )}
                      </div>
                      <h4 className="fw-bold plan-price">
                        <PriceDisplay price={checkOutPlan?.pricingTitle || 0} />
                      </h4>
                    </div>

                    <Formik
                      initialValues={initialValues}
                      validationSchema={validationSchema}
                      onSubmit={handleVerifyCouponCode}
                    >
                      {({
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        validateForm,
                        values,
                        errors,
                        isValid,
                        touched,
                        submitCount,
                        isSubmitting,
                        setFieldValue
                      }) => (
                        <Form
                          onSubmit={async (event) => {
                            event.preventDefault();
                            await validateForm();
                            handleSubmit();
                          }}
                        >
                          <Form.Group controlId="coupon" className="form-group">
                            <Form.Label className="d-flex align-items-center justify-content-between w-100">
                              <span>Coupon Code</span>
                              {isDisabled && (
                                <span
                                  role="button"
                                  className="text-danger text-decoration-underline fw-bold"
                                  onClick={() => {
                                    setFieldValue("coupon", "");
                                    setVerifyCodeResp(initCouponValues);
                                    setIsDisabled(false);
                                    setApplyCoupon(null);
                                    setValidCouponCode(null);
                                  }}
                                >
                                  Remove
                                </span>
                              )}
                            </Form.Label>

                            {!isDisabled && (
                              <div className="field-has-btn">
                                <Form.Control
                                  type="text"
                                  autoComplete="false"
                                  placeholder="Enter Coupon Code"
                                  name="coupon"
                                  value={values.coupon}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={Boolean(errors.coupon) && touched.coupon}
                                />
                                <Button
                                  variant="transparent"
                                  className="field-btn text-primary"
                                    type="submit"
                                    disabled={!values.coupon}
                                >
                                  {!isSubmitting ? "Apply Code" : <ButtonSpinner />}
                                </Button>
                              </div>
                            )}

                            {applyCoupon === true && (
                              <Alert variant="success" className="verification-response">
                                <div className="content-wrap">
                                  <FASvgIcon icon={faCircleCheck} />
                                  <div className="alert-content">
                                    <h3 className="fw-medium text-greyDark mb-0">
                                      Code applied:&nbsp;
                                      <strong className="text-secondary">{validCouponCode}</strong>
                                    </h3>
                                    <h4 className="fw-medium text-greyDark mb-0">
                                      You save&nbsp;
                                      <strong className="text-success">
                                        <PriceDisplay price={verifyCodeResp.discount} />
                                      </strong>
                                    </h4>
                                  </div>
                                </div>
                              </Alert>
                            )}

                            {applyCoupon === false && (
                              <Alert variant="danger" className="verification-response">
                                <div className="content-wrap">
                                  <FASvgIcon icon={faCircleXmark} />
                                  <div className="alert-content">
                                    <h3 className="fw-medium text-greyDark mb-0">
                                      Code entered:&nbsp;
                                      <strong className="text-danger">
                                        {validCouponCode || values.coupon}
                                      </strong>
                                    </h3>
                                    <h4 className="fw-medium text-greyDark mb-0">
                                      The Coupon Code in Invalid. Please try again.
                                    </h4>
                                  </div>
                                </div>
                              </Alert>
                            )}
                          </Form.Group>
                        </Form>
                      )}
                    </Formik>

                    <div className="checkout-details">
                      <h5 className="text-greyDark">Subtotal</h5>
                      <h5>
                        <PriceDisplay price={verifyCodeResp.subtotal} />
                      </h5>
                    </div>

                    {verifyCodeResp.discount ? (
                      <div className="checkout-details">
                        <h5 className="text-greyDark">Discount</h5>
                        <h5 className="text-success">
                          <PriceDisplay price={verifyCodeResp.discount * -1} allowNegative={true} />
                        </h5>
                      </div>
                    ) : null}

                    <hr />
                    <div className="checkout-details">
                      <h4 className="fw-bold">Total</h4>
                      <h4 className="fw-bold">
                        <PriceDisplay price={verifyCodeResp.total} />
                      </h4>
                    </div>
                    <Button className="mx-auto btn-w-150" onClick={() => handlePlanPurchase()}>
                      {!isProcessing ? (
                        <>
                          Pay Now
                          <FASvgIcon icon={faArrowRight} />
                        </>
                      ) : (
                        <ButtonSpinner />
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {selectedPlanData && (
        <RazorPayPayment
          loadRazorpay={!!selectedPlanData}
          filingPlanData={selectedPlanData}
          handleOnPaymentSuccess={() => {
            setSelectedPlanData(() => null);
            dispatch(unsetCheckOutPlan());
            navigate("/payment-successful");
          }}
          handleOnPaymentCancel={() => {
            setSelectedPlanData(() => null);
          }}
          handleErrorOnInit={() => {
            setSelectedPlanData(() => null);
          }}
        />
      )}
    </div>
  );
};

export default PlanCheckout;
